// 🔧 pages/public/terms.js
// ✅ Purpose: Display the Let's Go Vote Terms and Conditions
// ✅ Features:
// - Clear, styled T&C content
// - Covers privacy, data access, event promoter agreements, and more
// - Includes icons per section for visual clarity
// - Public-facing, accessible by anyone

import React from "react";
import SeoHead from "../../components/seo_head";
import { PUBLIC_PAGE_SEO } from "../../constants/marketing_pages";

const Terms = () => {
  return (
  <>
    <SeoHead {...PUBLIC_PAGE_SEO.terms} />

      <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">📄 Terms & Conditions</h1>
          <p className="lead text-muted">Please read this carefully before registering or using Let's Go Vote</p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        <h4 className="mb-3">👥 User Data & Privacy</h4>
        <ul>
          <li>Let’s Go Vote does <strong>not sell, rent, or share your personal information</strong> with third parties.</li>
          <li>Your registration information is only visible to authorized administrators and managers of the event(s) you participate in.</li>
          <li>Each event promoter must sign a <strong>confidentiality agreement</strong> before being granted access to event-specific user data.</li>
          <li>If you choose to opt-in to event updates, event promoters may use your contact information to share event news, future event invites, or related updates.</li>
        </ul>

        <h4 className="mt-5 mb-3">🗳️ Use of the Platform</h4>
        <ul>
          <li>This app is designed to facilitate fair and transparent voting at community events.</li>
          <li>Users are expected to vote only once per category and avoid any tampering or duplicate voting attempts.</li>
        </ul>

        <h4 className="mt-5 mb-3">⚖️ Voting Integrity</h4>
        <ul>
          <li>
            Let’s Go Vote may monitor voting activity to detect abuse,
            duplicate registrations, or attempts to manipulate event results.
          </li>
          <li>
            Event administrators may review voting activity when investigating
            suspected fraud or rule violations.
          </li>
          <li>
            Users found intentionally abusing the system may have their votes
            removed or their accounts restricted.
          </li>
        </ul>

        <h4 className="mt-5 mb-3">🚗 Event Entries</h4>
        <ul>
          <li>
            Some events may allow participants to register vehicles, projects,
            pets, artwork, or other entries through the platform.
          </li>
          <li>
            Event organizers may limit the number of entries per participant.
          </li>
          <li>
            Participants are responsible for ensuring submitted information is accurate.
          </li>
        </ul>

        <h4 className="mt-5 mb-3">🙋 Volunteer Activities</h4>
        <ul>
          <li>
            Some events may offer volunteer opportunities through the platform.
          </li>
          <li>
            Volunteer schedules and assignments are managed by event organizers.
          </li>
          <li>
            Participants are responsible for providing accurate contact
            information for volunteer communications.
          </li>
        </ul>

        <h4 className="mt-5 mb-3">🔐 Security</h4>
        <ul>
          <li>We take reasonable measures to ensure the security of your data while it's stored or transferred.</li>
          <li>You are responsible for safeguarding your login credentials and not sharing access with others.</li>
        </ul>

        <h4 className="mt-5 mb-3">📬 Communication</h4>
        <ul>
          <li>If you opt in to updates, you may receive emails from the event promoter related to the event or future events they organize.</li>
          <li>You can revoke permission at any time by contacting the event promoter or our support team.</li>
        </ul>

        <h4 className="mt-5 mb-3">📱 Optional SMS Alerts</h4>
        <ul>
          <li>Auction SMS alerts require separate, express consent and are not a condition of purchase.</li>
          <li>With consent, LGV may send transactional Auction messages about being outbid, winning, payment receipt, and pickup readiness. Message frequency varies. Message and data rates may apply.</li>
          <li>Reply STOP to opt out or HELP for help. See the <a href="/sms-alerts">SMS Alerts Terms</a> for the current program details.</li>
        </ul>

        <h4 className="mt-5 mb-3">🏷️ Auctions & Donations</h4>
        <ul>
          <li>
            Some events may use Let's Go Vote to conduct silent auctions,
            fundraising activities, raffles, or donation campaigns.
          </li>
          <li>
            Event organizers are responsible for fulfillment of auction items,
            prize distribution, and payment collection.
          </li>
          <li>
            Let's Go Vote provides technology services and is not responsible
            for disputes between participants and event organizers.
          </li>
        </ul>

        <h4 className="mt-5 mb-3">📅 Policy Updates</h4>
        <ul>
          <li>We may update these Terms from time to time. Continued use of the platform after updates implies acceptance.</li>
        </ul>

        <h4 className="mt-5 mb-3">📧 Questions</h4>
        <p>
          Questions regarding these Terms & Conditions may be directed to the
          Let's Go Vote support team.
        </p>

        <p className="mt-5 small text-muted">
          Effective Date: May 2025<br />
          Last Updated: October 29, 2026
        </p>

      </div>
    </section>
    </>
  );
};

export default Terms;
